import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import prettyBytes from "pretty-bytes";
// @ts-ignore -- react-toastify package.json exports issue
import { toast } from "react-toastify";

import Modal from "@/components/modal";
import useApiClient from "@/hooks/useApiClient";
import { mapDataAssetSearchParamsToRequestBody } from "@/hooks/dataAssetSearchParams";
import { ApiError, type DataAssetSearchParameters } from "@/apiClient";

type SaveMode = "create" | "update";

type SaveSuccessPayload = {
  action: SaveMode;
  dataSetName: string;
  publicId: string;
};

type DataSetSaveModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (payload: SaveSuccessPayload) => void;
  resultCount: number;
  totalDataVolume: number;
  searchParams: Record<string, unknown>;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof ApiError) {
    return error.body?.detail || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong while saving your data set.";
};

const DataSetSaveModal = ({
  isOpen,
  onClose,
  onSuccess,
  resultCount,
  totalDataVolume,
  searchParams,
}: DataSetSaveModalProps) => {
  const api = useApiClient();
  const [mode, setMode] = useState<SaveMode>("create");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [datasetQuery, setDatasetQuery] = useState("");
  const [selectedDataSetPublicId, setSelectedDataSetPublicId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMode("create");
    setName("");
    setDescription("");
    setDatasetQuery("");
    setSelectedDataSetPublicId("");
    setIsSubmitting(false);
  }, [isOpen]);

  const requestBody = useMemo(
    () =>
      mapDataAssetSearchParamsToRequestBody(
        searchParams
      ) as DataAssetSearchParameters,
    [searchParams]
  );

  const { data: dataSets = [], isLoading: dataSetsAreLoading } = useQuery({
    queryKey: ["data-set-save-modal", datasetQuery],
    queryFn: () =>
      api.dataAccess
        .listDataSets({
          query: datasetQuery || undefined,
          size: 8,
        })
        .then((response) => response.items),
    enabled: isOpen && mode === "update",
    refetchOnWindowFocus: false,
  });

  const selectedDataSet = dataSets.find(
    (dataSet) => dataSet.public_id === selectedDataSetPublicId
  );

  const canSubmit =
    !isSubmitting &&
    resultCount > 0 &&
    ((mode === "create" && name.trim().length > 0) ||
      (mode === "update" && selectedDataSetPublicId.length > 0));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);

    try {
      if (mode === "create") {
        const created = await api.dataAccess.createDataSet({
          requestBody: {
            name: name.trim(),
            description: description.trim() || undefined,
            number_of_assets: 0,
            total_data_volume: 0,
          },
        });

        await api.dataAccess.bulkAddAssetsFromSearch({
          dataSetPublicId: created.public_id!,
          requestBody,
        });

        toast.success(`Created data set "${created.name}".`);
        onSuccess({
          action: "create",
          dataSetName: created.name,
          publicId: created.public_id!,
        });
      } else if (selectedDataSet) {
        await api.dataAccess.bulkAddAssetsFromSearch({
          dataSetPublicId: selectedDataSet.public_id!,
          requestBody,
        });

        toast.success(`Updated data set "${selectedDataSet.name}".`);
        onSuccess({
          action: "update",
          dataSetName: selectedDataSet.name,
          publicId: selectedDataSet.public_id!,
        });
      }

      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-6">
          <div>
            <Dialog.Title className="text-3xl font-semibold text-blue-300">
              Save search results as a data set
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-base text-grey-200">
              {resultCount.toLocaleString()} assets selected, totaling{" "}
              {prettyBytes(totalDataVolume)}
            </Dialog.Description>
          </div>
          <button
            aria-label="Close data set save modal"
            className="text-3xl leading-none text-grey-200 transition hover:text-blue-300"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="mt-6 inline-flex rounded-full border border-gray-300 bg-gray-50 p-1">
          <button
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              mode === "create"
                ? "bg-blue-100 text-white"
                : "text-blue-300 hover:bg-white"
            }`}
            onClick={() => setMode("create")}
            type="button"
          >
            Create new
          </button>
          <button
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              mode === "update"
                ? "bg-blue-100 text-white"
                : "text-blue-300 hover:bg-white"
            }`}
            onClick={() => setMode("update")}
            type="button"
          >
            Add to existing
          </button>
        </div>

        {mode === "create" ? (
          <div className="mt-6 space-y-5">
            <label className="block">
              <span className="mb-2 block text-lg font-semibold text-blue-300">
                Data set name
              </span>
              <input
                className="form-input block w-full rounded-md border-gray-300 text-blue-300 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                onChange={(event) => setName(event.target.value)}
                placeholder="Enter a unique data set name"
                type="text"
                value={name}
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-lg font-semibold text-blue-300">
                Description
              </span>
              <textarea
                className="form-textarea block min-h-[8rem] w-full rounded-md border-gray-300 text-blue-300 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Describe what this data set captures"
                value={description}
              />
            </label>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <label className="block">
              <span className="mb-2 block text-lg font-semibold text-blue-300">
                Find a data set
              </span>
              <input
                className="form-input block w-full rounded-md border-gray-300 text-blue-300 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                onChange={(event) => setDatasetQuery(event.target.value)}
                placeholder="Search by name or description"
                type="text"
                value={datasetQuery}
              />
            </label>

            <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
              {dataSetsAreLoading ? (
                <p className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-grey-200">
                  Loading data sets...
                </p>
              ) : dataSets.length > 0 ? (
                dataSets.map((dataSet) => {
                  const isSelected = dataSet.public_id === selectedDataSetPublicId;
                  const assetCount = dataSet.number_of_assets ?? 0;
                  const totalDataVolume = dataSet.total_data_volume ?? 0;
                  return (
                    <button
                      className={`w-full rounded-lg border px-4 py-3 text-left shadow-sm transition ${
                        isSelected
                          ? "border-blue-100 bg-blue-50"
                          : "border-gray-200 bg-white hover:border-blue-100 hover:bg-gray-50"
                      }`}
                      key={dataSet.public_id}
                      onClick={() => setSelectedDataSetPublicId(dataSet.public_id ?? "")}
                      type="button"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-lg font-semibold text-blue-300">
                            {dataSet.name}
                          </p>
                          <p className="mt-1 text-sm text-grey-200">
                            {dataSet.description || "No description yet."}
                          </p>
                        </div>
                        <div className="shrink-0 text-right text-xs uppercase tracking-wide text-grey-200">
                          <p>{assetCount.toLocaleString()} assets</p>
                          <p>{prettyBytes(totalDataVolume)}</p>
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-grey-200">
                  No matching data sets found.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-end gap-3">
          <button
            className="rounded-lg bg-gray-200 px-4 py-2 text-sm text-gray-800 transition-colors hover:bg-gray-300"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors ${
              canSubmit
                ? "bg-blue-100 hover:bg-blue-200"
                : "cursor-not-allowed bg-gray-400 opacity-50"
            }`}
            disabled={!canSubmit}
            onClick={handleSubmit}
            type="button"
          >
            {isSubmitting
              ? "Saving..."
              : mode === "create"
              ? "Create data set"
              : "Add to data set"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DataSetSaveModal;
